import React from 'react';
import { Text } from '../Text';
import { TableToolbar } from '../TableToolbar';
import { Calendar } from '../Calendar';
import { Button } from '../Button';
import { Box } from '../Box';
import { CalendarPanelFooterProps } from './CalendarPanelFooter.types';

type ParsedSelectedDays = Date | { from: Date; to?: Date };

const isSelectedDaysRange = (
  selectedDays: ParsedSelectedDays,
): selectedDays is Extract<ParsedSelectedDays, { from: Date; to?: Date }> => {
  return !(selectedDays instanceof Date);
};

const getSelectedDaysString = (
  selectedDaysRaw: CalendarPanelFooterProps['selectedDays'],
  dateToString: CalendarPanelFooterProps['dateToString'],
) => {
  if (!selectedDaysRaw) {
    return '';
  }

  const selectedDays: ParsedSelectedDays = Calendar.parseValue(selectedDaysRaw);

  if (isSelectedDaysRange(selectedDays)) {
    const toSuffix = selectedDays.to ? ` ${dateToString(selectedDays.to)}` : '';
    return `${dateToString(selectedDays.from)} -${toSuffix}`;
  }

  return dateToString(selectedDays);
};

const CalendarPanelFooter = ({
  dataHook,
  dateToString,
  secondaryActionLabel,
  primaryActionLabel,
  selectedDays,
  primaryActionDisabled,
  primaryActionOnClick,
  secondaryActionOnClick,
}: CalendarPanelFooterProps) => {
  return (
    <div data-hook={dataHook}>
      <TableToolbar>
        <TableToolbar.ItemGroup position="start">
          <TableToolbar.Item>
            <Text
              size="small"
              weight="thin"
              secondary
              dataHook="selected-days-text"
            >
              {getSelectedDaysString(selectedDays, dateToString)}
            </Text>
          </TableToolbar.Item>
        </TableToolbar.ItemGroup>
        <TableToolbar.ItemGroup position="end">
          <Box>
            <Button
              size="small"
              priority="secondary"
              dataHook="secondary-action-button"
              onClick={secondaryActionOnClick}
            >
              {secondaryActionLabel}
            </Button>
            <Box margin="0 6px" />
            <Button
              size="small"
              disabled={primaryActionDisabled}
              dataHook="primary-action-button"
              onClick={primaryActionOnClick}
            >
              {primaryActionLabel}
            </Button>
          </Box>
        </TableToolbar.ItemGroup>
      </TableToolbar>
    </div>
  );
};

CalendarPanelFooter.displayName = 'CalendarPanelFooter';

export { CalendarPanelFooter };
